import { origin } from '../BackBar';
import { Notice } from '@verlo/shared/components/ui/notice';
import { Signal } from '@verlo/shared/components/ui/signal';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@verlo/shared/components/ui/button';
import { ordersApi, kitchenApi, tablesApi, reservationsApi } from '@verlo/shared/services/api';
import wsService, { WS_EVENTS } from '@verlo/shared/services/websocket';
import { toast } from 'sonner';

// The Orders board — what needs a manager right now (backend/attention.py). Rose for food
// and money waiting on someone, amber for time slipping; each card has one thing to do.
// Refetches on every order / table / ticket event and once a minute for the ages to tick.

const POLL_MS = 60000;

const RAIL = { high: 'bg-signal-stop-line', warn: 'bg-signal-warn-line' };
const TONE = { high: 'border-signal-stop-line', warn: 'border-signal-warn-line' };

const AttentionBoard = ({ onChanged }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null); // card key being acted on

  const load = useCallback(async () => {
    try {
      const res = await ordersApi.attention();
      setData(res.data);
    } catch {
      /* the ledger below still works; the board just doesn't refresh */
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, POLL_MS);
    wsService.connect();
    const unsubs = [
      WS_EVENTS.ORDER_CREATED, WS_EVENTS.ORDER_UPDATED, WS_EVENTS.ORDER_STATUS_CHANGED,
      WS_EVENTS.TICKET_UPDATED, WS_EVENTS.TABLE_STATUS_CHANGED, WS_EVENTS.TABLE_OPENED, WS_EVENTS.TABLE_CLOSED,
    ].filter(Boolean).map((ev) => wsService.subscribe(ev, () => load()));
    return () => { clearInterval(t); unsubs.forEach((u) => u?.()); };
  }, [load]);

  const act = async (card, action) => {
    const key = `${card.kind}:${card.ticket_id || card.table_id || card.reservation_id}:${action.key}`;
    setBusy(key);
    try {
      switch (action.key) {
        case 'run':
          await kitchenApi.updateTicketStatus(card.ticket_id, 'delivered');
          toast.success(`${card.table_name || 'Order'}: food marked delivered`);
          break;
        case 'kitchen':
          navigate('/kitchen', { state: origin('/orders', 'Orders') });
          return;
        case 'tables':
          navigate('/tables');
          return;
        case 'bookings':
          navigate('/bookings');
          return;
        case 'pay':
        case 'bill':
        case 'open':
          navigate(card.table_id ? `/tables/${card.table_id}/order` : `/orders/${card.order_id}`, { state: origin('/orders', 'Orders') });
          return;
        case 'clear':
          await tablesApi.close(card.table_id);
          toast.success(`${card.table_name} cleared`);
          break;
        case 'extend':
          await tablesApi.addTime(card.table_id, 30);
          toast.success(`${card.table_name}: 30 more minutes`);
          break;
        case 'arrived':
          await reservationsApi.arrive(card.reservation_id);
          toast.success('Marked as arrived');
          break;
        case 'noshow':
          await reservationsApi.noshow(card.reservation_id);
          toast.success('Marked no-show');
          break;
        default:
          return;
      }
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't do that");
    } finally {
      setBusy(null);
    }
  };

  const cards = data?.cards || [];
  const high = data?.counts?.high || 0;

  return (
    <div className="space-y-2" data-testid="attention-board">
      <div className="flex items-baseline justify-between">
        <h2 className="text-sm font-semibold text-ink-soft uppercase tracking-wide">
          Needs attention
          {cards.length > 0 && (
            <Signal tone={high ? 'stop' : 'warn'} className="ml-2 align-middle">{cards.length}</Signal>
          )}
        </h2>
        {data && <span className="text-[11px] text-ink-faint">as of {new Date(data.as_of).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</span>}
      </div>

      {data && cards.length === 0 && (
        <Notice tone="ok" word="Clear" data-testid="attention-clear">Nothing needs you right now.</Notice>
      )}

      {cards.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3">
          {cards.map((c) => {
            const id = `${c.kind}:${c.ticket_id || c.table_id || c.reservation_id || 'group'}`;
            return (
              <div key={id} className={`flex rounded-xl border bg-white overflow-hidden ${TONE[c.severity]}`} data-testid={`attention-${c.kind}`}>
                <div className={`w-1 shrink-0 ${RAIL[c.severity]}`} />
                <div className="flex-1 min-w-0 p-3">
                  <p className="text-[13px] font-semibold text-ink truncate">{c.title}</p>
                  <p className="text-[12px] text-ink-soft truncate">{c.detail}</p>
                  <div className="mt-2 flex gap-1.5">
                    {c.actions.map((a, i) => {
                      const key = `${id}:${a.key}`;
                      const primary = i === 0;             // the server puts the main action first
                      const danger = a.key === 'noshow';
                      return (
                        <Button
                          key={a.key}
                          size="sm"
                          variant={primary && !danger ? 'default' : 'outline'}
                          className={`h-8 text-[12px] ${primary && !danger ? 'bg-verlo hover:bg-verlo-hover' : ''} ${danger ? 'text-signal-stop border-signal-stop-line hover:bg-signal-stop-tint hover:text-signal-stop' : ''}`}
                          disabled={busy === key}
                          onClick={() => act(c, a)}
                          data-testid={`attention-action-${a.key}`}
                        >
                          {a.label}
                        </Button>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AttentionBoard;
